import { useEffect, useMemo, useState } from "react";
import { describeTimezone, formatTime, groupSlotsByDay } from "../services/schedulerApi";

interface SlotPickerProps {
  slots: string[];
  timeZone: string;
  selected: string | null;
  onSelect: (slot: string) => void;
  disabled?: boolean;
}

const SlotPicker: React.FC<SlotPickerProps> = ({ slots, timeZone, selected, onSelect, disabled }) => {
  const days = useMemo(() => groupSlotsByDay(slots, timeZone), [slots, timeZone]);
  const [activeDay, setActiveDay] = useState<string | null>(days[0]?.day ?? null);

  useEffect(() => {
    // Keep the chosen day valid when the slot list refreshes (for example after a slot was taken).
    if (!days.some((d) => d.day === activeDay)) setActiveDay(days[0]?.day ?? null);
  }, [days, activeDay]);

  if (days.length === 0) {
    return (
      <p className="text-text-secondary">
        There are no open times in the next few weeks. Please email{" "}
        <a className="text-primary underline" href="mailto:hello@contextral.com">hello@contextral.com</a> and we will
        find a time that works.
      </p>
    );
  }

  const current = days.find((d) => d.day === activeDay) ?? days[0];

  return (
    <div>
      <p className="text-sm text-text-secondary mb-4">Times shown in {describeTimezone(timeZone)}</p>
      <div role="tablist" aria-label="Available days" className="flex gap-2 overflow-x-auto pb-2 mb-5">
        {days.map((d) => (
          <button
            key={d.day}
            type="button"
            role="tab"
            aria-selected={d.day === current.day}
            onClick={() => setActiveDay(d.day)}
            disabled={disabled}
            className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
              d.day === current.day
                ? "border-primary bg-primary text-text-inverse"
                : "border-borderSubtle bg-white text-text-primary hover:bg-surfaceSoft"
            }`}
          >
            {d.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={current.label} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {current.slots.map((slot) => (
          <button
            key={slot}
            type="button"
            aria-pressed={slot === selected}
            onClick={() => onSelect(slot)}
            disabled={disabled}
            className={`rounded-lg border px-3 py-3 text-sm font-semibold transition-colors disabled:opacity-60 ${
              slot === selected
                ? "border-primary bg-primary/10 text-primary"
                : "border-borderSubtle bg-white text-text-primary hover:border-primary"
            }`}
          >
            {formatTime(slot, timeZone)}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SlotPicker;
